import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { CurrentUser } from 'models/User'
import useKrpano from 'pages/building/panoramas/hooks/useKrpano'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  isRefetchingAnnotationsState,
  listPointState,
  pendingDisplayImageState,
  revalidateFileListState,
  revalidatePointDetailState,
  selectedAreaState,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { displayToggleState } from 'store/displayToggleStore'
import { currentPovState } from 'store/krpanoStore'
import useSWR from 'swr'
import { Point, SenceData, SenceDataResponse } from 'types/building'
import Krpano from 'utils/krpano'

import BCPAnnotationModal from './components/BCPAnnotationModal/BCPAnnotationModal'
import DirectionSetting from './components/DirectionSetting'
import AnnotationModal from './components/ModalAddAnnotation'
import ModalAddStairway from './components/ModalAddStairway'
import usePreviewKrpano from './hooks/usePreviewKrpano'

export default function AreaPanorama() {
  const { id, floorId } = useParams()
  const { t } = useTranslation()
  const { getDirectory } = useDirectory()

  const [selectedPoint, setSelectedPoint] = useState('')

  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [revalidatePointDetail, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [isRefetchingAnnotations, setIsRefetchingAnnotations] = useRecoilState(
    isRefetchingAnnotationsState
  )
  const [, setDisplayToggle] = useRecoilState(displayToggleState)
  const [selectedArea, setSelectedArea] = useRecoilState(selectedAreaState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const points = useRecoilValue(listPointState)
  const currentPov = useRecoilValue(currentPovState)
  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)
  const [pendingDisplayImage] = useRecoilState(pendingDisplayImageState)

  const { trigger: getSenceData } = useMutation(`v1/points/file-info`)
  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me', {
    revalidateOnMount: false
  })

  const { mutate: refetchPoints } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId: id,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const {
    embedpano: embedFull,
    currentSelectedPoint,
    currentSelectedAnnotation
  } = useKrpano({
    buildingMode: buildingMode.selectedMode
  })

  const waitForEmbedPano = (): Promise<any> => {
    return new Promise((resolve) => {
      const check = () => {
        const embedpano = document.getElementById('embedpano-full') as any
        if (embedpano && embedpano.get) {
          resolve(embedpano)
        } else {
          setTimeout(check, 100)
        }
      }
      check()
    })
  }

  const handlePreloadSence = async () => {
    const embedpano = await waitForEmbedPano()

    const listPointTo = points
      .find((item) => item.id === currentSelectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentSelectedPoint
            )?.image360?.id

            const items: SenceData[] = []
            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length > 0) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  const isVisibleFull =
    buildingMode.previewMode && Boolean(currentSelectedPoint)

  const { embedpano: embedPreview } = usePreviewKrpano()

  // get point detail when select point
  const { mutate } = useSWR<Point>(
    selectedPoint && points
      ? {
          url: `v1/points/${selectedPoint}`,
          params: {
            building_id: id,
            floor_id: floorId
          }
        }
      : null,
    {
      onSuccess: async (data) => {
        setPointDetail(data)
        if (data?.areaId !== selectedArea) {
          setSelectedArea(data?.areaId)
        }
        setRevalidatePointDetail(undefined)
        if (data?.image360) {
          const isLocked = data?.isLocked && data?.lockedBy === profile?.id
          const isRefetchList = points?.find(
            (item) =>
              item.id === data.id &&
              (item?.lockedBy !== data?.lockedBy ||
                item?.isLocked !== data?.isLocked)
          )

          // Only update annotations if we're refetching for annotation updates
          if (isRefetchingAnnotations) {
            const annotations = data?.annotations?.filter((item) => {
              const viewingImage = data?.subImages?.find(
                (sub) => sub.id === selectedSubImage
              )
                ? selectedSubImage
                : data?.image360?.id
              return !item.imageID || item.imageID === viewingImage
            })

            // Update only annotations without changing view
            const krpano = document.getElementById('embedpano-full') as any
            if (krpano) {
              // Remove old annotations and add new ones
              annotations.forEach((annotation) => {
                krpano.call(`remove_annotation(${annotation.id})`)
              })
              Krpano.AddAnnotation(krpano, annotations, null)
            }

            setIsRefetchingAnnotations(false)
          } else {
            // Normal refetch - update everything including view
            const initView = currentPov || data?.initView
            const initProps = {
              imageUrl: data?.image360?.s3Path,
              previewUrl: data?.image360?.multires
                ? `${data?.image360?.tilesUrl}preview.jpg`
                : data?.image360?.thumbnail,
              isLocked,
              hgap: data?.initView?.radarHGap,
              multires: data?.image360?.multires,
              tilesUrl: data?.image360?.tilesUrl,
              ...initView
            }
            const annotations = data?.annotations?.filter((item) => {
              const viewingImage = data?.subImages?.find(
                (sub) => sub.id === selectedSubImage
              )
                ? selectedSubImage
                : data?.image360?.id
              return !item.imageID || item.imageID === viewingImage
            })
            embedFull(initProps, annotations, data?.directions, data?.stairways)
            embedPreview(initProps)
            await handlePreloadSence()
          }

          isRefetchList && refetchPoints()
          setDisplayToggle({
            isLocked: data?.isLocked,
            lockedBy: data?.lockedBy
          })
        } else {
          Krpano.UnmountFull()
        }
      },
      onError: (err) => {
        if (err?.response?.data?.error?.key === 'point_not_exists_error') {
          refetchPoints()
          setRevalidateFileList(1)
          notification.error({
            message: t('translation.pointAlreadyDeleted')
          })
        }
      }
    }
  )

  // handle change preview when select subImage
  useEffect(() => {
    if (
      selectedSubImage &&
      pendingDisplayImage &&
      selectedSubImage === pendingDisplayImage.id
    ) {
      const initView = currentPov || pendingDisplayImage?.initView
      const initProps = {
        imageUrl: pendingDisplayImage?.s3Path,
        previewUrl: pendingDisplayImage?.multires
          ? `${pendingDisplayImage?.tilesUrl}preview.jpg`
          : pendingDisplayImage?.thumbnail,
        h: initView?.h || 0,
        v: initView?.v || 0,
        z: initView?.z || 0,
        hgap: pendingDisplayImage?.initView?.radarHGap || 0,
        multires: pendingDisplayImage?.multires,
        tilesUrl: pendingDisplayImage?.tilesUrl
      }
      embedPreview(initProps)
    } else if (pointDetail) {
      const initView = currentPov || pointDetail.initView
      const initProps = {
        imageUrl: pointDetail.image360?.s3Path || '',
        previewUrl: pointDetail.image360?.multires
          ? `${pointDetail.image360?.tilesUrl}preview.jpg`
          : pointDetail.image360?.thumbnail || '',
        h: initView?.h || 0,
        v: initView?.v || 0,
        z: initView?.z || 0,
        hgap: pointDetail.initView?.radarHGap || 0,
        multires: pointDetail.image360?.multires,
        tilesUrl: pointDetail.image360?.tilesUrl
      }
      embedPreview(initProps)
    }
  }, [selectedSubImage, selectedPoint, currentPov])

  useEffect(() => {
    if (
      revalidatePointDetail &&
      currentSelectedPoint === revalidatePointDetail
    ) {
      mutate()
      return
    }
    if (revalidatePointDetail && selectedPoint && !currentSelectedPoint) {
      setSelectedPoint('')
      setPointDetail(undefined)
      setRevalidatePointDetail(undefined)
      Krpano.UnmountFull()
      return
    }
    setRevalidatePointDetail(undefined)
  }, [
    currentSelectedPoint,
    mutate,
    pointDetail?.id,
    revalidatePointDetail,
    selectedPoint,
    setPointDetail,
    setRevalidatePointDetail
  ])

  useEffect(() => {
    if (currentSelectedPoint) {
      setSelectedPoint(currentSelectedPoint)
    }
  }, [currentSelectedPoint])

  const krpanoContentLayout =
    buildingMode.selectedMode === 'direction'
      ? 'absolute top-0 w-full bottom-[135px] bg-[#F9FBFF] border border-4 border-solid theme-border-sub-primary'
      : `absolute h-full w-full ${!isVisibleFull ? 'invisible' : ''}`

  return (
    <>
      <div id="krpano-container" className={krpanoContentLayout} />

      {currentSelectedAnnotation && getDirectory() !== 'bcp' && (
        <AnnotationModal />
      )}
      {currentSelectedAnnotation && getDirectory() === 'bcp' && (
        <BCPAnnotationModal />
      )}

      <ModalAddStairway />

      {buildingMode.selectedMode === 'direction' && (
        <DirectionSetting
          currentSelectedPoint={currentSelectedPoint}
          setBuildingMode={setBuildingMode}
        />
      )}
    </>
  )
}
